/**
 * Griffle design tokens
 *
 * Single source of truth for all colours, spacing, radius and typography
 * used across the Griffle skin in maneno-web-kit.
 *
 * NOTE: These tokens are sized for a *business/admin* context — not the
 * full-screen children's reading app. Font sizes are smaller than the
 * maneno-react equivalents.
 */

// ─── Colour palette ────────────────────────────────────────────────────────────
export const GRIFFLE_COLORS = {
  /** Primary brand green */
  primary: "#539a18",
  /** Hover / active shade */
  primaryDark: "#3f7a10",
  /** Sidebar background — deep forest green */
  sidebarBg: "#1b3a0a",
  /** Sidebar hover */
  sidebarHover: "rgba(255,255,255,0.07)",
  /** Sidebar active item */
  sidebarActive: "rgba(83,154,24,0.28)",
  /** Sidebar text (dimmed) */
  sidebarText: "#a8c890",
  /** Sidebar text active */
  sidebarTextActive: "#ffffff",
  /** App background */
  pageBg: "#f4f7f1",
  /** Card / panel surface */
  surface: "#ffffff",
  /** Main body text */
  ink: "#1a1a1a",
  /** Secondary / muted text */
  inkMuted: "#52525b",
  /** Hairline dividers */
  border: "#e4ede1",
  /** Header background */
  headerBg: "#ffffff",
  /** Header border */
  headerBorder: "#e4ede1",
  /** Pure white */
  white: "#ffffff",
  /** Accent blue (links, charts) */
  accent: "#539a18",
  /** Danger red */
  danger: "#dc2626"
} as const;

// ─── Typography ────────────────────────────────────────────────────────────────
export const GRIFFLE_FONT = "'Sour Gummy', sans-serif";
export const GRIFFLE_FONT_BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export const GRIFFLE_TYPE = {
  // Logo / display
  logoSize: "26px",
  logoWeight: 900,
  // Page headings
  headingLg: "22px",
  headingMd: "18px",
  headingSm: "15px",
  // Body copy (use Inter for body; Sour Gummy for headings only)
  body: "14px",
  bodyMd: "13px",
  small: "12px",
  xsmall: "11px",
  // Weights
  weightLight: 400,
  weightMd: 500,
  weightSemi: 600,
  weightBold: 700,
  weightBlack: 900
} as const;

// ─── Spacing ────────────────────────────────────────────────────────────────────
export const GRIFFLE_SPACE = {
  pagePadding: "24px 32px",
  cardGap: "16px",
  sectionGap: "24px",
  headerHeight: "60px",
  sidebarWidth: "220px"
} as const;

// ─── Border radius ──────────────────────────────────────────────────────────────
export const GRIFFLE_RADIUS = {
  pill: "20px",
  pillLg: "24px",
  card: "10px",
  bookCover: "6px",
  circle: "50%",
  badge: "4px",
  button: "8px"
} as const;

// ─── Shadows ────────────────────────────────────────────────────────────────────
export const GRIFFLE_SHADOW = {
  card: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
  cardHover: "0 4px 12px rgba(0,0,0,0.12)",
  header: "0 1px 0 #e4ede1",
  button: "0 2px 8px rgba(83,154,24,0.3)"
} as const;

// ─── Stat card accent colours ───────────────────────────────────────────────────
export const GRIFFLE_STAT_COLORS = {
  students: { bg: "#edf7e4", accent: "#539a18", text: "#1b3a0a" },
  books: { bg: "#fff8e7", accent: "#f59e0b", text: "#78350f" },
  minutes: { bg: "#e8f4ff", accent: "#3b82f6", text: "#1e3a5f" },
  quizzes: { bg: "#fef2f2", accent: "#ef4444", text: "#7f1d1d" }
} as const;
